import React, { useState } from "react";

import {
	Button,
	ConfirmModal,
	PasswordField,
	SectionCard,
} from "@amphore-dev/amphore-lib";
import { AxiosError } from "axios";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import UserService from "@/services/UserService";

import { useAuth } from "@/hooks/useAuth";

import { getApiErrormessage } from "@/utils/UAPIErrors";

import { cn } from "@/utils";

interface IDeleteAccountCardProps {
	/** The account has a password to type; a provider-only account has none. */
	hasPassword: boolean;
	className?: string;
}

/**
 * The end of the profile page: deleting the account. A confirmation
 * modal that says what goes and what stays, asks the password when
 * there is one, then signs out. The API does the deleting — Stripe
 * first — and a refusal (wrong password, Stripe down) is shown as is.
 */
export const DeleteAccountCard: React.FC<IDeleteAccountCardProps> = ({
	hasPassword,
	className,
}) => {
	const { logout } = useAuth();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const [password, setPassword] = useState("");
	const [error, setError] = useState<string | undefined>();

	const close = () => {
		setOpen(false);
		setPassword("");
		setError(undefined);
	};

	const confirm = async () => {
		setError(undefined);
		try {
			await UserService.deleteMe(hasPassword ? { password } : {});
		} catch (err) {
			setError(getApiErrormessage(err as AxiosError));
			return;
		}
		toast.success(t("account.delete.done"));
		logout();
		navigate("/login");
	};

	return (
		<SectionCard
			title={t("account.delete.title")}
			className={cn(["border-2 border-red-600", className])}
		>
			<div className="flex flex-col gap-3">
				<p className="text-sm text-neutral-600">
					{t("account.delete.description")}
				</p>
				<Button
					color="danger"
					variant="outline"
					className="self-start"
					onClick={() => setOpen(true)}
				>
					{t("account.delete.action")}
				</Button>
			</div>
			<ConfirmModal
				open={open}
				onClose={close}
				onConfirm={confirm}
				title={t("account.delete.confirm.title")}
				confirmText={t("account.delete.confirm.confirm")}
				confirmColor="danger"
				size="sm"
			>
				<div className="flex flex-col gap-4">
					<p className="text-sm">
						{t("account.delete.confirm.goes")}
					</p>
					<p className="text-sm text-neutral-600">
						{t("account.delete.confirm.stays")}
					</p>
					{hasPassword && (
						<PasswordField
							name="deletePassword"
							label={t("account.delete.confirm.password")}
							autoComplete="current-password"
							value={password}
							onChange={setPassword}
							error={error}
						/>
					)}
					{!hasPassword && error && (
						<p className="text-sm text-red-600" role="alert">
							{error}
						</p>
					)}
				</div>
			</ConfirmModal>
		</SectionCard>
	);
};
